// M13 Slice 5b — Sektör Paketi Editörü: tree view + override drawer + save.
// Parent framework'ün tree'sini gösterir, her section/question için:
//   - Checkbox: include/exclude (local state, draft'ta editable)
//   - "Override" butonu: 4 metin alanını override etmek için drawer aç
// Save tuşunda backend'e PATCH gönderir (optimistic locking).

import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Edit3 } from "lucide-react";
import type {
  FrameworkVersionTreeResponse,
  QuestionOverride,
  ResolvedSectorPackage,
  SectionOverride,
  SectorPackageEditabilityResponse,
  SectorPackageSummary,
} from "@mahir/shared";
import { api } from "../../api/client";
import {
  archiveSectorPackage,
  deleteSectorPackage,
  getResolvedSectorPackage,
  getSectorPackage,
  getSectorPackageEditability,
  publishSectorPackage,
  updateSectorPackage,
} from "../../api/sector-packages";
import { OverrideDrawer } from "./OverrideDrawer";
import { UpgradeModal } from "./UpgradeModal";

interface Props {
  packageId: string;
  onChanged: () => void;
  onDeleted: () => void;
}

interface EditState {
  excludedSectionCodes: Set<string>;
  excludedQuestionCodes: Set<string>;
  sectionOverrides: Record<string, SectionOverride>;
  questionOverrides: Record<string, QuestionOverride>;
}

type DrawerTarget =
  | { kind: "section"; code: string; defaultLabel: string; defaultPrompt: string }
  | {
      kind: "question";
      compoundCode: string;
      defaultLabel: string;
      defaultAiContext: string | null;
    }
  | null;

export function SectorPackageEditor({ packageId, onChanged, onDeleted }: Props): JSX.Element {
  const [pkg, setPkg] = useState<SectorPackageSummary | null>(null);
  const [tree, setTree] = useState<FrameworkVersionTreeResponse | null>(null);
  const [resolved, setResolved] = useState<ResolvedSectorPackage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyAction, setBusyAction] = useState<string | null>(null);

  const [editState, setEditState] = useState<EditState | null>(null);
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());
  const [drawer, setDrawer] = useState<DrawerTarget>(null);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [editability, setEditability] = useState<SectorPackageEditabilityResponse | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { package: p } = await getSectorPackage(packageId);
      setPkg(p);

      // Edit state'i paketten initialize et
      setEditState({
        excludedSectionCodes: new Set(p.excludedSectionCodes),
        excludedQuestionCodes: new Set(p.excludedQuestionCodes),
        sectionOverrides: { ...p.sectionOverrides },
        questionOverrides: { ...p.questionOverrides },
      });

      // Parent tree'yi paralel çek
      const treeRes = await api<FrameworkVersionTreeResponse>(
        `/admin/frameworks/${p.parentFrameworkId}/versions/${p.parentFrameworkVersionId}/tree`,
      ).catch((err: unknown) => {
        console.warn("tree load failed:", err);
        return null;
      });
      setTree(treeRes);

      // Resolved view (orphan tespit için)
      const resolvedRes = await getResolvedSectorPackage(packageId).catch((err: unknown) => {
        console.warn("resolved load failed:", err);
        return null;
      });
      setResolved(resolvedRes);

      // Orphan-edit eligibility (draft her zaman edit, published orphan-only)
      const eligRes = await getSectorPackageEditability(packageId).catch(() => null);
      setEditability(eligRes);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, [packageId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const isDraft = pkg?.status === "draft";
  // M13 — Tree edit'i draft VEYA orphan-published'da aç.
  // Backend updateSectorPackage zaten archived'a 409 atıyor.
  const canEdit =
    pkg?.status === "draft" || (pkg?.status === "published" && editability?.editable === true);

  const isDirty = useMemo(() => {
    if (!pkg || !editState) return false;
    const sameExcSec =
      pkg.excludedSectionCodes.length === editState.excludedSectionCodes.size &&
      pkg.excludedSectionCodes.every((c) => editState.excludedSectionCodes.has(c));
    const sameExcQ =
      pkg.excludedQuestionCodes.length === editState.excludedQuestionCodes.size &&
      pkg.excludedQuestionCodes.every((c) => editState.excludedQuestionCodes.has(c));
    const sameSecOv =
      JSON.stringify(pkg.sectionOverrides) === JSON.stringify(editState.sectionOverrides);
    const sameQOv =
      JSON.stringify(pkg.questionOverrides) === JSON.stringify(editState.questionOverrides);
    return !(sameExcSec && sameExcQ && sameSecOv && sameQOv);
  }, [pkg, editState]);

  const toggleSection = useCallback((code: string) => {
    setEditState((s) => {
      if (!s) return s;
      const next = new Set(s.excludedSectionCodes);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return { ...s, excludedSectionCodes: next };
    });
  }, []);

  const toggleQuestion = useCallback((compoundCode: string) => {
    setEditState((s) => {
      if (!s) return s;
      const next = new Set(s.excludedQuestionCodes);
      if (next.has(compoundCode)) next.delete(compoundCode);
      else next.add(compoundCode);
      return { ...s, excludedQuestionCodes: next };
    });
  }, []);

  const toggleCollapse = useCallback((sectionId: string) => {
    setCollapsedSections((c) => {
      const next = new Set(c);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
  }, []);

  const handleSectionOverrideSave = useCallback(
    (code: string, override: SectionOverride | null) => {
      setEditState((s) => {
        if (!s) return s;
        const next = { ...s.sectionOverrides };
        if (override === null) {
          delete next[code];
        } else {
          next[code] = override;
        }
        return { ...s, sectionOverrides: next };
      });
      setDrawer(null);
    },
    [],
  );

  const handleQuestionOverrideSave = useCallback(
    (compoundCode: string, override: QuestionOverride | null) => {
      setEditState((s) => {
        if (!s) return s;
        const next = { ...s.questionOverrides };
        if (override === null) {
          delete next[compoundCode];
        } else {
          next[compoundCode] = override;
        }
        return { ...s, questionOverrides: next };
      });
      setDrawer(null);
    },
    [],
  );

  const handleSave = useCallback(async () => {
    if (!pkg || !editState) return;
    setBusyAction("save");
    setError(null);
    try {
      await updateSectorPackage(packageId, {
        expectedUpdatedAt: pkg.updatedAt,
        excludedSectionCodes: [...editState.excludedSectionCodes],
        excludedQuestionCodes: [...editState.excludedQuestionCodes],
        sectionOverrides: editState.sectionOverrides,
        questionOverrides: editState.questionOverrides,
      });
      onChanged();
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Kaydedilemedi");
    } finally {
      setBusyAction(null);
    }
  }, [packageId, pkg, editState, onChanged, reload]);

  const runAction = useCallback(
    async (name: string, fn: () => Promise<unknown>) => {
      setBusyAction(name);
      setError(null);
      try {
        await fn();
        onChanged();
        await reload();
      } catch (err) {
        setError(err instanceof Error ? err.message : "İşlem başarısız");
      } finally {
        setBusyAction(null);
      }
    },
    [onChanged, reload],
  );

  if (loading) {
    return <div style={{ color: "var(--color-text-3)" }}>Yükleniyor…</div>;
  }
  if (error || !pkg || !editState) {
    return (
      <div style={{ color: "var(--color-error)", padding: 16 }}>{error ?? "Paket yüklenemedi"}</div>
    );
  }

  const orphanCount = resolved
    ? resolved.orphans.excludedSectionCodes.length +
      resolved.orphans.excludedQuestionCodes.length +
      resolved.orphans.sectionOverrideKeys.length +
      resolved.orphans.questionOverrideKeys.length
    : 0;

  // Resolved özet — local edit state üzerinden compute
  let liveSectionCount = 0;
  let liveQuestionCount = 0;
  let liveRequiredCount = 0;
  if (tree) {
    for (const sec of tree.sections) {
      if (editState.excludedSectionCodes.has(sec.code)) continue;
      liveSectionCount++;
      for (const q of sec.questions) {
        const compound = `${sec.code}.${q.code}`;
        if (editState.excludedQuestionCodes.has(compound)) continue;
        liveQuestionCount++;
        if (q.required && q.enabled) liveRequiredCount++;
      }
    }
  }

  return (
    <div>
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 16,
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        <div style={{ flex: "1 1 260px", minWidth: 0 }}>
          <h2
            style={{
              fontFamily: "var(--font-display)",
              fontSize: "1.4rem",
              marginBottom: 4,
            }}
          >
            {pkg.name}
          </h2>
          <div style={{ fontSize: "0.85rem", color: "var(--color-text-2)" }}>
            <span style={{ fontFamily: "var(--font-mono)" }}>{pkg.slug}</span>
            {" · "}
            {pkg.parentFrameworkName} {pkg.parentVersionNumber}
            {" · "}
            <StatusBadge status={pkg.status} />
          </div>
          {pkg.description && (
            <p className="mt-2 text-sm" style={{ color: "var(--color-text-2)" }}>
              {pkg.description}
            </p>
          )}
        </div>
        <div style={{ display: "flex", gap: 8, flexShrink: 0, flexWrap: "wrap" }}>
          {/* Kaydet: draft + orphan-published'da görünür. */}
          {canEdit && (
            <button
              onClick={() => void handleSave()}
              disabled={busyAction !== null || !isDirty}
              style={primaryBtn(busyAction === "save" || !isDirty)}
            >
              {busyAction === "save" ? "Kaydediliyor…" : isDirty ? "Kaydet" : "Kaydedildi"}
            </button>
          )}
          {/* Yayınla: sadece draft → published transition (orphan-published zaten yayında). */}
          {isDraft && (
            <button
              onClick={() => {
                void runAction("publish", async () => {
                  await publishSectorPackage(packageId);
                });
              }}
              disabled={busyAction !== null || orphanCount > 0 || isDirty}
              style={primaryBtn(busyAction === "publish" || orphanCount > 0 || isDirty)}
              title={
                orphanCount > 0
                  ? "Orphan referansları temizleyin önce"
                  : isDirty
                    ? "Değişiklikleri kaydedin önce"
                    : undefined
              }
            >
              {busyAction === "publish" ? "Yayınlanıyor…" : "Yayınla"}
            </button>
          )}
          {pkg.status === "published" && (
            <button
              onClick={() => {
                void runAction("archive", async () => {
                  await archiveSectorPackage(packageId);
                });
              }}
              disabled={busyAction !== null}
              style={secondaryBtn(busyAction === "archive")}
            >
              {busyAction === "archive" ? "Arşivleniyor…" : "Arşivle"}
            </button>
          )}
          <button
            onClick={() => {
              const ok = window.confirm(
                `"${pkg.name}" paketini silmek üzeresiniz.\n\nSoft-delete uygulanacak. Devam edilsin mi?`,
              );
              if (!ok) return;
              void runAction("delete", async () => {
                await deleteSectorPackage(packageId);
                onDeleted();
              });
            }}
            disabled={busyAction !== null}
            style={dangerBtn(busyAction === "delete")}
          >
            Sil
          </button>
        </div>
      </div>

      {/* Outdated banner */}
      {pkg.isOutdated && (
        <div
          style={{
            ...bannerStyle("warning"),
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
          }}
        >
          <div>
            <strong>Üst çerçeve güncellendi.</strong> Bu paket{" "}
            <strong>{pkg.parentVersionNumber}</strong> sürümüne bağlı. Yeni sürüme yükseltilebilir.
          </div>
          <button
            onClick={() => setUpgradeOpen(true)}
            style={{
              padding: "6px 12px",
              borderRadius: 6,
              border: "none",
              background: "var(--color-warning, #f0ad4e)",
              color: "white",
              fontSize: "0.85rem",
              fontWeight: 600,
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            Yükselt
          </button>
        </div>
      )}

      {/* Orphan-published eligibility banner — published paket projeye atanmadıysa düzenlenebilir */}
      {editability?.status === "published" && (
        <div
          style={{
            ...bannerStyle(editability.editable ? "warning" : "lock"),
            marginBottom: 16,
          }}
        >
          {editability.editable ? (
            <>
              <strong>🟡 Yayında — düzenlenebilir.</strong> Bu paket henüz hiçbir projeye atanmamış.
              Tree'yi değiştirebilir, override ekleyebilir, kaydedebilirsiniz. Bir proje açılır
              açılmaz değişiklik otomatik kilitlenir.
            </>
          ) : (
            <>
              <strong>🔒 Kilitli.</strong> Bu paket {editability.projectCount.toString()} projede
              kullanılıyor — yayında sürüm değişiklikleri korunuyor.
            </>
          )}
        </div>
      )}

      {/* Orphan banner */}
      {orphanCount > 0 && (
        <div style={bannerStyle("error")}>
          <strong>⚠ {orphanCount.toString()} orphan referans var.</strong> Üst çerçevede artık
          olmayan exclusion/override key'leri. Yayınlamak için temizleyin.
          {resolved && (
            <div className="mt-2 text-xs">
              {resolved.orphans.excludedSectionCodes.length > 0 && (
                <div>
                  Bölüm exclude: <code>{resolved.orphans.excludedSectionCodes.join(", ")}</code>
                </div>
              )}
              {resolved.orphans.excludedQuestionCodes.length > 0 && (
                <div>
                  Soru exclude: <code>{resolved.orphans.excludedQuestionCodes.join(", ")}</code>
                </div>
              )}
              {resolved.orphans.sectionOverrideKeys.length > 0 && (
                <div>
                  Bölüm override: <code>{resolved.orphans.sectionOverrideKeys.join(", ")}</code>
                </div>
              )}
              {resolved.orphans.questionOverrideKeys.length > 0 && (
                <div>
                  Soru override: <code>{resolved.orphans.questionOverrideKeys.join(", ")}</code>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Resolved özet */}
      {tree && (
        <div
          style={{
            padding: 12,
            background: "var(--color-bg)",
            border: "1px solid var(--color-border)",
            borderRadius: 8,
            marginBottom: 16,
            fontSize: "0.85rem",
            color: "var(--color-text-2)",
          }}
        >
          Resolved özet: <strong>{liveSectionCount.toString()}</strong> bölüm /{" "}
          <strong>{liveQuestionCount.toString()}</strong> soru (
          <strong>{liveRequiredCount.toString()}</strong> zorunlu+aktif)
          {liveSectionCount === 0 && (
            <span style={{ color: "var(--color-error)", marginLeft: 8 }}>
              ⚠ En az 1 bölüm dahil edilmeli
            </span>
          )}
          {liveRequiredCount === 0 && liveSectionCount > 0 && (
            <span style={{ color: "var(--color-error)", marginLeft: 8 }}>
              ⚠ En az 1 zorunlu+aktif soru dahil edilmeli
            </span>
          )}
        </div>
      )}

      {/* Tree */}
      {!tree ? (
        <div
          style={{
            padding: 16,
            color: "var(--color-text-3)",
            fontStyle: "italic",
            border: "1px dashed var(--color-border)",
            borderRadius: 8,
          }}
        >
          Üst çerçeve ağacı yüklenemedi.
        </div>
      ) : (
        <div
          style={{
            border: "1px solid var(--color-border)",
            borderRadius: 8,
            overflow: "hidden",
          }}
        >
          {tree.sections.map((sec) => {
            const sectionExcluded = editState.excludedSectionCodes.has(sec.code);
            const isCollapsed = collapsedSections.has(sec.id);
            const hasSecOverride = !!editState.sectionOverrides[sec.code];
            return (
              <div
                key={sec.id}
                style={{
                  borderBottom: "1px solid var(--color-border)",
                  background: sectionExcluded ? "var(--color-bg)" : "var(--color-surface)",
                  opacity: sectionExcluded ? 0.55 : 1,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "10px 12px",
                    gap: 10,
                  }}
                >
                  <button
                    onClick={() => toggleCollapse(sec.id)}
                    style={{
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                      padding: 2,
                      color: "var(--color-text-2)",
                    }}
                    aria-label={isCollapsed ? "Aç" : "Daralt"}
                  >
                    {isCollapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                  </button>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      cursor: canEdit ? "pointer" : "not-allowed",
                      flex: 1,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={!sectionExcluded}
                      onChange={() => toggleSection(sec.code)}
                      disabled={!canEdit}
                    />
                    <span style={{ fontWeight: 600 }}>
                      {editState.sectionOverrides[sec.code]?.label ?? sec.label}
                    </span>
                    <span
                      style={{
                        fontFamily: "var(--font-mono)",
                        fontSize: "0.75rem",
                        color: "var(--color-text-3)",
                      }}
                    >
                      {sec.code}
                    </span>
                    {hasSecOverride && (
                      <span
                        style={{
                          fontSize: "0.7rem",
                          padding: "1px 6px",
                          borderRadius: 4,
                          background: "color-mix(in srgb, var(--color-primary) 15%, white)",
                          color: "var(--color-primary)",
                        }}
                      >
                        Değiştirilmiş
                      </span>
                    )}
                  </label>
                  {canEdit && (
                    <button
                      onClick={() =>
                        setDrawer({
                          kind: "section",
                          code: sec.code,
                          defaultLabel: sec.label,
                          defaultPrompt: sec.sectionPrompt,
                        })
                      }
                      style={iconBtn()}
                      title="Override"
                    >
                      <Edit3 size={14} />
                    </button>
                  )}
                </div>

                {!isCollapsed && (
                  <div style={{ paddingLeft: 36, paddingRight: 12, paddingBottom: 8 }}>
                    {sec.questions.map((q) => {
                      const compound = `${sec.code}.${q.code}`;
                      const qExcluded =
                        sectionExcluded || editState.excludedQuestionCodes.has(compound);
                      const hasQOverride = !!editState.questionOverrides[compound];
                      return (
                        <div
                          key={q.id}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            padding: "4px 0",
                            gap: 8,
                            opacity: qExcluded ? 0.55 : 1,
                          }}
                        >
                          <label
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                              flex: 1,
                              cursor: canEdit && !sectionExcluded ? "pointer" : "not-allowed",
                              fontSize: "0.9rem",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={!qExcluded}
                              onChange={() => toggleQuestion(compound)}
                              disabled={!canEdit || sectionExcluded}
                            />
                            <span>{editState.questionOverrides[compound]?.label ?? q.label}</span>
                            <span
                              style={{
                                fontFamily: "var(--font-mono)",
                                fontSize: "0.72rem",
                                color: "var(--color-text-3)",
                              }}
                            >
                              {compound}
                            </span>
                            {q.required && (
                              <span
                                style={{
                                  fontSize: "0.7rem",
                                  color: "var(--color-text-3)",
                                }}
                              >
                                (zorunlu)
                              </span>
                            )}
                            {hasQOverride && (
                              <span
                                style={{
                                  fontSize: "0.68rem",
                                  padding: "0 5px",
                                  borderRadius: 3,
                                  background: "color-mix(in srgb, var(--color-primary) 12%, white)",
                                  color: "var(--color-primary)",
                                }}
                              >
                                Değiştirilmiş
                              </span>
                            )}
                          </label>
                          {canEdit && (
                            <button
                              onClick={() =>
                                setDrawer({
                                  kind: "question",
                                  compoundCode: compound,
                                  defaultLabel: q.label,
                                  defaultAiContext: q.aiContext,
                                })
                              }
                              style={iconBtn()}
                              title="Override"
                            >
                              <Edit3 size={12} />
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Override Drawer */}
      {drawer?.kind === "section" && (
        <OverrideDrawer
          kind="section"
          targetLabel={drawer.defaultLabel}
          targetCode={drawer.code}
          defaults={{ label: drawer.defaultLabel, prompt: drawer.defaultPrompt }}
          current={editState.sectionOverrides[drawer.code]}
          onClose={() => setDrawer(null)}
          onSave={(ov) => handleSectionOverrideSave(drawer.code, ov)}
        />
      )}
      {drawer?.kind === "question" && (
        <OverrideDrawer
          kind="question"
          targetLabel={drawer.defaultLabel}
          targetCode={drawer.compoundCode}
          defaults={{ label: drawer.defaultLabel, aiContext: drawer.defaultAiContext ?? "" }}
          current={editState.questionOverrides[drawer.compoundCode]}
          onClose={() => setDrawer(null)}
          onSave={(ov) => handleQuestionOverrideSave(drawer.compoundCode, ov)}
        />
      )}

      {/* Upgrade modal (Slice 5c) */}
      {upgradeOpen && (
        <UpgradeModal
          packageId={packageId}
          parentFrameworkId={pkg.parentFrameworkId}
          currentParentVersionId={pkg.parentFrameworkVersionId}
          currentParentVersionNumber={pkg.parentVersionNumber}
          onClose={() => setUpgradeOpen(false)}
          onUpgraded={() => {
            onChanged();
            void reload();
          }}
        />
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: "draft" | "published" | "archived" }): JSX.Element {
  const map = {
    draft: { label: "Taslak", color: "var(--color-text-3)" },
    published: { label: "Yayında", color: "var(--color-success, #2f7a3e)" },
    archived: { label: "Arşivde", color: "var(--color-text-3)" },
  } as const;
  const { label, color } = map[status];
  return <span style={{ color, fontWeight: 600 }}>{label}</span>;
}

function bannerStyle(kind: "warning" | "error" | "lock"): React.CSSProperties {
  const color =
    kind === "warning"
      ? "var(--color-warning, #f0ad4e)"
      : kind === "error"
        ? "var(--color-error)"
        : "var(--color-text-3)";
  return {
    padding: 12,
    borderRadius: 8,
    background: `color-mix(in srgb, ${color} 10%, white)`,
    border: `1px solid ${color}`,
    marginBottom: 16,
    fontSize: "0.9rem",
  };
}

function primaryBtn(disabled: boolean): React.CSSProperties {
  return {
    padding: "8px 14px",
    borderRadius: 6,
    border: "none",
    background: "var(--color-primary)",
    color: "white",
    fontSize: "0.88rem",
    fontWeight: 600,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.5 : 1,
  };
}

function secondaryBtn(disabled: boolean): React.CSSProperties {
  return {
    padding: "8px 14px",
    borderRadius: 6,
    border: "1px solid var(--color-border)",
    background: "transparent",
    color: "var(--color-text)",
    fontSize: "0.88rem",
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.5 : 1,
  };
}

function dangerBtn(disabled: boolean): React.CSSProperties {
  return {
    padding: "8px 14px",
    borderRadius: 6,
    border: "1px solid var(--color-error)",
    background: "transparent",
    color: "var(--color-error)",
    fontSize: "0.88rem",
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.5 : 1,
  };
}

function iconBtn(): React.CSSProperties {
  return {
    padding: 6,
    borderRadius: 4,
    border: "1px solid var(--color-border)",
    background: "transparent",
    color: "var(--color-text-2)",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
  };
}
